import React, { useState, useEffect } from 'react';
import { zbioAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { BiocryptPay } from '@biocrypt/gateway';
import './ZbioWallet.css';

const ZbioWallet: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [depositAmount, setDepositAmount] = useState<string>('');
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [withdrawAddress, setWithdrawAddress] = useState<string>('');
  const [platformInfo, setPlatformInfo] = useState<any>(null);
  const [myAddress, setMyAddress] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadBalance();
    loadPlatformInfo();
    loadMyWallet();
  }, []);

  const loadMyWallet = async () => {
    try {
      const res = await zbioAPI.getMyWallet();
      setMyAddress(res.data.address);
    } catch (e) {
      console.error('Error loading wallet address:', e);
    }
  };

  const loadBalance = async () => {
    try {
      const res = await zbioAPI.getBalance();
      setBalance(res.data.balance);
    } catch (e) {
      console.error('Error loading balance:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadPlatformInfo = async () => {
    try {
      const res = await zbioAPI.getPlatformInfo();
      setPlatformInfo(res.data);
    } catch (e) {
      console.error('Error loading platform info:', e);
    }
  };

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      setMessage({ type: 'error', text: t('wallet.enterValidAmount') });
      return;
    }

    setProcessing(true);
    setMessage(null);

    try {
      // Create deposit on our backend
      const depositRes = await zbioAPI.createDeposit(amount);
      const { depositId, recipientPublicKeyHash, gatewayApi } = depositRes.data;

      // Open BiocryptPay popup for the user to pay
      const pay = new BiocryptPay({ networkUrl: gatewayApi.replace('/api', '') });
      const result = await pay.requestPayment({
        amount,
        to: recipientPublicKeyHash,
        description: `Tellcrypt deposit: ${amount} ZBIO`,
      });

      if (result.success) {
        // Confirm the deposit on our backend
        const confirmRes = await zbioAPI.confirmDeposit(depositId, result.paymentId);
        setBalance(confirmRes.data.newBalance);
        setMessage({ type: 'success', text: t('wallet.depositSuccess', { amount }) });
        setDepositAmount('');
      } else {
        setMessage({ type: 'error', text: result.error || t('wallet.paymentNotCompleted') });
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.message || t('wallet.depositFailed') });
    } finally {
      setProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      setMessage({ type: 'error', text: t('wallet.enterValidAmount') });
      return;
    }
    if (!withdrawAddress || withdrawAddress.length < 16) {
      setMessage({ type: 'error', text: t('wallet.enterValidAddress') });
      return;
    }

    setProcessing(true);
    setMessage(null);

    try {
      const res = await zbioAPI.withdraw(amount, withdrawAddress);
      setBalance(res.data.newBalance);
      setMessage({ type: 'success', text: `${t('wallet.withdrawSuccess', { amount })} (tx: ${res.data.txId.slice(0, 12)}...)` });
      setWithdrawAmount('');
      setWithdrawAddress('');
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.message || t('wallet.withdrawFailed') });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-modern">
        <div className="zbio-wallet-loading">{t('wallet.loadingWallet')}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-modern zbio-page">
      <div className="dashboard-header">
        <div>
          <h1>{t('wallet.title')}</h1>
          <p>{t('wallet.pageSubtitle')}</p>
        </div>
        <div className="zbio-balance-display zbio-balance-header">
          <span className="zbio-balance-amount">{balance}</span>
          <span className="zbio-balance-label">ZBIO</span>
        </div>
      </div>

      <div className="zbio-wallet">
      {message && (
        <div className={`zbio-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {myAddress && (
        <div className="zbio-section zbio-receive">
          <h4>{t('wallet.yourAddress')}</h4>
          <p className="zbio-description">
            {t('wallet.yourAddressDesc')}
          </p>
          <div
            className="zbio-address-box"
            title={t('wallet.clickToCopy')}
            onClick={() => {
              navigator.clipboard?.writeText(myAddress);
              setMessage({ type: 'success', text: t('wallet.addressCopied') });
            }}
          >
            <code>{myAddress}</code>
            <span className="zbio-copy-hint">📋 {t('wallet.copy')}</span>
          </div>
        </div>
      )}

      <div className="zbio-sections">
        {/* Deposit Section */}
        <div className="zbio-section">
          <h4>{t('wallet.depositTitle')}</h4>
          <p className="zbio-description">
            {t('wallet.depositDesc')}
          </p>
          <div className="zbio-input-row">
            <input
              type="number"
              min="1"
              step="1"
              placeholder={t('wallet.amount')}
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              className="zbio-input"
              disabled={processing}
            />
            <button
              className="zbio-btn deposit"
              onClick={handleDeposit}
              disabled={processing || !depositAmount}
            >
              {processing ? t('wallet.processing') : t('wallet.deposit')}
            </button>
          </div>
        </div>

        {/* Withdraw Section */}
        <div className="zbio-section">
          <h4>{t('wallet.withdrawTitle')}</h4>
          <p className="zbio-description">
            {t('wallet.withdrawDesc')}
          </p>
          <div className="zbio-input-row">
            <input
              type="number"
              min="1"
              step="1"
              placeholder={t('wallet.amount')}
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="zbio-input"
              disabled={processing}
            />
          </div>
          <div className="zbio-input-row">
            <input
              type="text"
              placeholder={t('wallet.addressPlaceholder')}
              value={withdrawAddress}
              onChange={(e) => setWithdrawAddress(e.target.value)}
              className="zbio-input address"
              disabled={processing}
            />
            <button
              className="zbio-btn withdraw"
              onClick={handleWithdraw}
              disabled={processing || !withdrawAmount || !withdrawAddress}
            >
              {processing ? t('wallet.processing') : t('wallet.withdraw')}
            </button>
          </div>
        </div>
      </div>

      {platformInfo && (
        <div className="zbio-footer">
          <span className="zbio-network-badge">
            {t('wallet.biocryptNetwork')}
          </span>
          <a
            href="https://www.biocrypt.net"
            target="_blank"
            rel="noopener noreferrer"
            className="zbio-link"
          >
            {t('wallet.getZbio')}
          </a>
        </div>
      )}
      </div>
    </div>
  );
};

export default ZbioWallet;
